import { useEffect } from "react";

import { datadogRum } from "@datadog/browser-rum";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTenantFlags, useTenantSetting } from "@nestoca/multi-tenant";
import {
  ErrorMessage,
  Flex,
  FormElement,
  Input,
  type ModalProps,
  PhoneInput,
  debounce,
} from "@nestoca/ui";
import { useGetAccount, useMutateAccount } from "@shared/api/hooks/account";
import {
  SettingsFormData,
  settingsSchema,
  TOAST_AUTOCLOSE_DELAY_IN_MS,
} from "@shared/constants";
import { KeepI18ExprHappy } from "@shared/utils";
import Debug from "debug";
import { useRouter } from "next/router";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { AccountManagementSection } from "./account-management-section";
import styles from "./user-settings-modal.module.scss";

const debug = Debug("nesto:user-settings");
const DEBOUNCE_TIME = 500;

export const SettingsForm = ({ onClose }: Pick<ModalProps, "onClose">) => {
  const { t } = useTranslation();
  const { enableEditAccountInfo } = useTenantFlags();
  const {
    settings: { showAccountManagement = true },
  } = useTenantSetting("cmaFormSettings");
  const { mutate: mutateAccount, isPending: isMutatingAccount } =
    useMutateAccount();
  const { locale } = useRouter();
  const { data: account } = useGetAccount();
  const methods = useForm<SettingsFormData>({
    reValidateMode: "onChange",
    resolver: zodResolver(settingsSchema),
    defaultValues: {
      firstName: account?.firstName || "",
      lastName: account?.lastName || "",
      phone: account?.phone,
      email: account?.email || "",
    },
  });

  const {
    register,
    control,
    formState: { errors },
  } = methods;

  useEffect(() => {
    methods.reset({
      firstName: account?.firstName || "",
      lastName: account?.lastName || "",
      phone: account?.phone,
      email: account?.email || "",
    });
  }, [account, methods]);

  const handleSubmit = methods.handleSubmit((formData: SettingsFormData) => {
    if (!account) {
      return toast(t("failedToSave", { ns: "error" }), { type: "error" });
    }

    mutateAccount(
      {
        ...account,
        ...formData,
        phoneSpecified: !!formData.phone,
      },
      {
        onSuccess: () => {
          debug("onSubmit onSuccess");
          methods.reset(formData);
          toast(t("dataUpdatedSuccess", { ns: "applications" }), {
            type: "success",
            autoClose: TOAST_AUTOCLOSE_DELAY_IN_MS,
          });
        },
        onError: (error) => {
          debug("onSubmit onError", error);
          toast(t("dataUpdatedFailure", { ns: "applications" }), {
            type: "error",
          });
          datadogRum.addError(error);
        },
      }
    );

    return formData;
  });

  const handlePhoneChange = debounce(() => handleSubmit(), DEBOUNCE_TIME);

  return (
    <Flex gap={2} direction="column">
      <FormProvider {...methods}>
        <Flex gap={2} direction="column" className={styles.section}>
          <h2 className={styles.title}>{t("name")}</h2>
          <Flex direction="column" gap={3}>
            <FormElement isInvalid={!!errors.firstName} isDisabled>
              <Input
                isDisabled
                placeholder={t("firstName") as string}
                e2ePrefix={"firstName"}
                {...register("firstName")}
              />
              {!!errors.firstName && (
                <ErrorMessage e2ePrefix={"firstName"}>
                  {t(`${errors.firstName.message}` as KeepI18ExprHappy)}
                </ErrorMessage>
              )}
            </FormElement>
            <FormElement isInvalid={!!errors.lastName} isDisabled>
              <Input
                isDisabled
                placeholder={t("lastName") as string}
                e2ePrefix={"lastName"}
                {...register("lastName")}
              />
              {!!errors.lastName && (
                <ErrorMessage e2ePrefix={"lastName"}>
                  {t(`${errors.lastName.message}` as KeepI18ExprHappy)}
                </ErrorMessage>
              )}
            </FormElement>
          </Flex>
        </Flex>

        <Flex gap={2} mt={4} direction="column" className={styles.section}>
          <h2 className={styles.title}>{t("contactDetails")}</h2>
          <Flex direction="column" gap={3}>
            <FormElement
              isInvalid={!!errors.phone}
              isDisabled={!enableEditAccountInfo}
            >
              <Controller
                control={control}
                name="phone"
                render={({ field }) => (
                  <PhoneInput
                    placeholder={t("userPhoneNumber")}
                    isDisabled={!enableEditAccountInfo || isMutatingAccount}
                    {...field}
                    onChange={(value) => {
                      field.onChange(value ?? "");
                      handlePhoneChange();
                    }}
                  />
                )}
              />
              {!!errors.phone && (
                <ErrorMessage e2ePrefix="phone">
                  {t(`${errors.phone.message}` as KeepI18ExprHappy)}
                </ErrorMessage>
              )}
            </FormElement>
            <FormElement isInvalid={!!errors.email} isDisabled>
              <Input
                isDisabled
                placeholder={t("emailAddress") as string}
                e2ePrefix={"email"}
                {...register("email")}
              />
              {!!errors.email && (
                <ErrorMessage e2ePrefix={"email"}>
                  {t(`${errors.email.message}` as KeepI18ExprHappy)}
                </ErrorMessage>
              )}
            </FormElement>
          </Flex>
        </Flex>
      </FormProvider>

      {showAccountManagement && (
        <AccountManagementSection onClose={onClose} locale={locale} />
      )}
    </Flex>
  );
};
